import {
    KeyEvent,
    KeyEventType,
    KeyHolder,
    KeyHolderKind,
    KeyKind,
    KeyReceipt,
    KeyRegisterData,
    KeyStatus,
    KeyType,
    KeyTypeAttachmentData,
    KeyTypeObjectLinks,
    KEY_EVENT_TYPES,
    KEY_HOLDER_KINDS,
    KEY_KINDS,
    KEY_STATUSES,
    RegisterKey,
} from '../types/keyRegister';
import { DATE_ONLY_RE } from '../Helpers/leaseUtils';
import { attachmentsNewestFirst } from '../Helpers/keyRegisterUtils';

/**
 * Pure parsers for the `keyRegister/` root: no Firebase, so other APIs (tenant deletion) can read raw
 * key nodes without pulling in `keyRegisterApi`. Malformed nodes log a warning and are dropped.
 */

const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) result[key] = entry;
    }
    return result as T;
};

export const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

export const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const byKey = <T extends { key: string }>(a: T, b: T): number => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);

export const parseKeyHolder = (value: unknown): KeyHolder | undefined => {
    const h = asRecord(value);
    return typeof h.kind === 'string' && KEY_HOLDER_KINDS.includes(h.kind as KeyHolderKind) && typeof h.key === 'string' && h.key.length > 0
        ? { kind: h.kind as KeyHolderKind, key: h.key }
        : undefined;
};

const parseReceipt = (value: unknown): KeyReceipt | undefined => {
    const r = asRecord(value);
    if (typeof r.storagePath !== 'string' || r.storagePath.length === 0) return undefined;
    return {
        kind: r.kind === 'signature' ? 'signature' : 'scan',
        storagePath: r.storagePath,
        author: typeof r.author === 'string' ? r.author : '',
        date: typeof r.date === 'string' ? r.date : '',
        width: typeof r.width === 'number' ? r.width : 0,
        height: typeof r.height === 'number' ? r.height : 0,
        fileSize: typeof r.fileSize === 'number' ? r.fileSize : 0,
    };
};

const parseAttachments = (value: unknown): { [attachmentKey: string]: KeyTypeAttachmentData } | undefined => {
    const attachments: { [attachmentKey: string]: KeyTypeAttachmentData } = {};
    for (const [attachmentKey, raw] of Object.entries(asRecord(value))) {
        const a = asRecord(raw);
        if (typeof a.storagePath !== 'string' || a.storagePath.length === 0) continue;
        attachments[attachmentKey] = {
            fileName: optionalString(a.fileName) ?? a.storagePath.split('/').pop() ?? '',
            mimeType: typeof a.mimeType === 'string' ? a.mimeType : '',
            storagePath: a.storagePath,
            author: typeof a.author === 'string' ? a.author : '',
            date: typeof a.date === 'string' ? a.date : '',
            width: typeof a.width === 'number' ? a.width : 0,
            height: typeof a.height === 'number' ? a.height : 0,
            fileSize: typeof a.fileSize === 'number' ? a.fileSize : 0,
        };
    }
    return Object.keys(attachments).length > 0 ? attachments : undefined;
};

/** `{objectKey: buildingKey}`; entries without a building key drop out, absent when empty. */
export const parseObjectLinks = (value: unknown): KeyTypeObjectLinks | undefined => {
    const links: KeyTypeObjectLinks = {};
    for (const [objectKey, buildingKey] of Object.entries(asRecord(value))) {
        if (typeof buildingKey === 'string' && buildingKey.trim()) links[objectKey] = buildingKey.trim();
    }
    return Object.keys(links).length > 0 ? links : undefined;
};

export const parseKeyType = (data: unknown, key: string): KeyType | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    const name = optionalString(d.name);
    if (!name || typeof d.kind !== 'string' || !KEY_KINDS.includes(d.kind as KeyKind)) {
        console.warn(`Invalid key type at ${key}: missing name or kind`);
        return null;
    }
    const opensBuildings: { [buildingKey: string]: true } = {};
    for (const [buildingKey, flag] of Object.entries(asRecord(d.opensBuildings))) {
        if (flag === true) opensBuildings[buildingKey] = true;
    }
    return stripUndefined({
        key,
        name,
        kind: d.kind as KeyKind,
        description: optionalString(d.description),
        lockSystemRef: optionalString(d.lockSystemRef),
        opensBuildings: Object.keys(opensBuildings).length > 0 ? opensBuildings : undefined,
        opensUnits: parseObjectLinks(d.opensUnits),
        opensStorageUnits: parseObjectLinks(d.opensStorageUnits),
        opens: optionalString(d.opens),
        archived: d.archived === true ? (true as const) : undefined,
        attachments: parseAttachments(d.attachments),
        createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
        createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
        updatedBy: optionalString(d.updatedBy),
        updatedWhen: optionalString(d.updatedWhen),
    });
};

export const parseKeyEvent = (data: unknown, key: string): KeyEvent | null => {
    const e = asRecord(data);
    if (typeof e.type !== 'string' || !KEY_EVENT_TYPES.includes(e.type as KeyEventType) || typeof e.actor !== 'string' || typeof e.when !== 'string') {
        return null;
    }
    return stripUndefined({
        key,
        type: e.type as KeyEventType,
        actor: e.actor,
        when: e.when,
        effectiveDate: typeof e.effectiveDate === 'string' && DATE_ONLY_RE.test(e.effectiveDate) ? e.effectiveDate : e.when.slice(0, 10),
        note: optionalString(e.note),
        holder: parseKeyHolder(e.holder),
        dueDate: typeof e.dueDate === 'string' && DATE_ONLY_RE.test(e.dueDate) ? e.dueDate : undefined,
        receipt: parseReceipt(e.receipt),
    });
};

export const parseRegisterKey = (data: unknown, key: string): RegisterKey | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (typeof d.typeKey !== 'string' || d.typeKey.length === 0 || typeof d.status !== 'string' || !KEY_STATUSES.includes(d.status as KeyStatus)) {
        console.warn(`Invalid key at ${key}: missing typeKey or status`);
        return null;
    }
    const status = d.status as KeyStatus;
    const log: KeyEvent[] = [];
    for (const [eventKey, raw] of Object.entries(asRecord(d.log))) {
        const event = parseKeyEvent(raw, eventKey);
        if (event) log.push(event);
    }
    return stripUndefined({
        key,
        typeKey: d.typeKey,
        serial: optionalString(d.serial),
        location: optionalString(d.location),
        status,
        // Holder and due date mean something only while handed out; a stale leftover is ignored.
        holder: status === 'handedOut' ? parseKeyHolder(d.holder) : undefined,
        dueDate: status === 'handedOut' && typeof d.dueDate === 'string' && DATE_ONLY_RE.test(d.dueDate) ? d.dueDate : undefined,
        log: log.sort((a, b) => (a.when < b.when ? -1 : a.when > b.when ? 1 : byKey(a, b))),
        createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
        createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
        updatedBy: optionalString(d.updatedBy),
        updatedWhen: optionalString(d.updatedWhen),
    });
};

/** The nodes under `keyRegister/keys` (or a query of it), parsed; malformed ones drop out. Sorted by key. */
export const parseRegisterKeys = (data: unknown): RegisterKey[] =>
    Object.entries(asRecord(data))
        .map(([key, value]) => parseRegisterKey(value, key))
        .filter((key): key is RegisterKey => key !== null)
        .sort(byKey);

export const parseKeyRegister = (data: unknown): KeyRegisterData => {
    const root = asRecord(data);
    const types: KeyType[] = [];
    for (const [key, value] of Object.entries(asRecord(root.types))) {
        const type = parseKeyType(value, key);
        if (type) types.push(type);
    }
    return {
        types: types.sort((a, b) => a.name.localeCompare(b.name, 'sv', { numeric: true })),
        keys: parseRegisterKeys(root.keys),
        lockSchedules: attachmentsNewestFirst(parseAttachments(root.lockSchedules)),
    };
};
